import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { EditorModule } from "primeng/editor";
import {
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from "rxjs";
import { environment } from "../../../environments/environment";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import {
  ensureBrCountryCode,
  normalizeFacebookInput,
  normalizeInstagramInput,
  normalizeWhatsappInput,
  parseFacebookUsername,
  parseInstagramUsername,
  parsePhoneFromWhatsappUrl,
  phoneToWhatsappUrl,
  usernameToFacebookUrl,
  usernameToInstagramUrl,
} from "../../../utils/social-links.helper";
import { AuthService } from "../../services/auth.service";
import { BlobStorageService } from "../../services/blob-storage.service";
import { SnackBarService } from "../../services/snack-bar.service";

type ProducerPageForm = {
  organizer_page: FormControl<string>;
  name: FormControl<string>;
  description: FormControl<string | null>;
  thumbnail: FormControl<File | null>;
  banner: FormControl<File | null>;
  past_events: FormControl<boolean>;
  // Cada campo guarda só o "handle" (username/dígitos); convertido para a URL
  // exigida pelo BE no submit.
  instagram_username: FormControl<string>;
  facebook_username: FormControl<string>;
  whatsapp_phone: FormControl<string>;
};

const INSTAGRAM_USERNAME_PATTERN = /^[a-zA-Z0-9._]{1,30}$/;
const FACEBOOK_USERNAME_PATTERN = /^[a-zA-Z0-9.-]+$/;
const WHATSAPP_PHONE_PATTERN = /^\d{10,15}$/;

@Component({
  selector: "app-create-producers-page",
  imports: [
    AppLayoutComponent,
    CommonModule,
    ReactiveFormsModule,
    EditorModule,
    MatTooltipModule,
    PageHeaderComponent,
    ButtonComponent,
  ],
  templateUrl: "./create-producers-page.component.html",
  styleUrl: "./create-producers-page.component.scss",
})
export class CreateProducersPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly blobService = inject(BlobStorageService);
  private readonly snackbarService = inject(SnackBarService);

  form!: FormGroup<ProducerPageForm>;
  controls!: ProducerPageForm;

  thumbnailPreview: string | ArrayBuffer | null = null;
  bannerPreview: string | ArrayBuffer | null = null;
  showThumbnailInput = true;
  showBannerInput = true;
  isSaving = false;

  private existingThumbnail: string | null = null;
  private existingBanner: string | null = null;

  ngOnInit(): void {
    const user = this.authService.getLocalStorageUserInfo();

    this.form = new FormGroup<ProducerPageForm>({
      organizer_page: new FormControl(user?.organizer_page ?? "", {
        nonNullable: true,
        validators: [Validators.required],
      }),
      name: new FormControl(user?.name ?? "", {
        nonNullable: true,
        validators: [Validators.required],
      }),
      description: new FormControl<string | null>(user?.description ?? null),
      thumbnail: new FormControl<File | null>(null),
      banner: new FormControl<File | null>(null),
      past_events: new FormControl(user?.past_events ?? false, {
        nonNullable: true,
      }),
      instagram_username: new FormControl(
        parseInstagramUsername(user?.instagram_url) ?? "",
        {
          nonNullable: true,
          validators: [Validators.pattern(INSTAGRAM_USERNAME_PATTERN)],
        },
      ),
      facebook_username: new FormControl(
        parseFacebookUsername(user?.facebook_url) ?? "",
        {
          nonNullable: true,
          validators: [Validators.pattern(FACEBOOK_USERNAME_PATTERN)],
        },
      ),
      whatsapp_phone: new FormControl(
        parsePhoneFromWhatsappUrl(user?.whatsapp_url) ?? "",
        {
          nonNullable: true,
          validators: [Validators.pattern(WHATSAPP_PHONE_PATTERN)],
        },
      ),
    });
    this.controls = this.form.controls;

    this.controls.instagram_username.valueChanges.subscribe((value) => {
      const normalized = normalizeInstagramInput(value);
      if (normalized !== value) {
        this.controls.instagram_username.setValue(normalized, {
          emitEvent: false,
        });
      }
    });

    this.controls.facebook_username.valueChanges.subscribe((value) => {
      const normalized = normalizeFacebookInput(value);
      if (normalized !== value) {
        this.controls.facebook_username.setValue(normalized, {
          emitEvent: false,
        });
      }
    });

    this.controls.whatsapp_phone.valueChanges.subscribe((value) => {
      const normalized = normalizeWhatsappInput(value);
      if (normalized !== value) {
        this.controls.whatsapp_phone.setValue(normalized, { emitEvent: false });
      }
    });

    if (user?.thumbnail) {
      this.existingThumbnail = user.thumbnail;
      this.thumbnailPreview = `${environment.BLOB_URL}/${user.thumbnail}`;
      this.showThumbnailInput = false;
    }

    if (user?.banner) {
      this.existingBanner = user.banner;
      this.bannerPreview = `${environment.BLOB_URL}/${user.banner}`;
      this.showBannerInput = false;
    }

    this.controls.organizer_page.valueChanges.subscribe((value) => {
      this.updateOrganizerPage(value);
    });
  }

  openProducerPage(): void {
    const slug = this.controls.organizer_page.value;
    if (!slug) return;
    window.open(`/${slug}`, "_blank");
  }

  onSubmit(): void {
    if (!this.form.valid || this.isSaving) return;

    const user = this.authService.getLocalStorageUserInfo();
    if (!user) {
      this.snackbarService.openSnackBar(
        "Usuário não encontrado. Faça login novamente.",
        "Fechar",
        "error",
      );
      return;
    }

    this.isSaving = true;

    const {
      organizer_page,
      name,
      description,
      thumbnail,
      banner,
      past_events,
      instagram_username,
      facebook_username,
      whatsapp_phone,
    } = this.form.getRawValue();

    const instagram_url = instagram_username
      ? usernameToInstagramUrl(instagram_username)
      : null;
    const facebook_url = facebook_username
      ? usernameToFacebookUrl(facebook_username)
      : null;
    const whatsapp_url = whatsapp_phone
      ? phoneToWhatsappUrl(ensureBrCountryCode(whatsapp_phone))
      : null;

    forkJoin({
      thumbnailPath: this.resolveImagePath$(
        thumbnail,
        "thumbnail",
        user.id,
        this.existingThumbnail,
      ),
      bannerPath: this.resolveImagePath$(
        banner,
        "banner",
        user.id,
        this.existingBanner,
      ),
    })
      .pipe(
        switchMap(({ thumbnailPath, bannerPath }) =>
          this.authService.updateUserInfo({
            user: {
              email: user.email,
              name,
              last_name: user.last_name,
              roles: user.roles,
              organizer_page,
              description,
              thumbnail: thumbnailPath,
              banner: bannerPath,
              past_events,
              instagram_url,
              facebook_url,
              whatsapp_url,
            },
          }),
        ),
        tap(() =>
          this.snackbarService.openSnackBar(
            "Página salva com sucesso.",
            "Fechar",
            "success",
          ),
        ),
        catchError(() => {
          this.snackbarService.openSnackBar(
            "Erro ao salvar a página.",
            "Fechar",
            "error",
          );
          return EMPTY;
        }),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe();
  }

  private resolveImagePath$(
    value: File | null,
    type: "thumbnail" | "banner",
    userId: number,
    existing: string | null,
  ): Observable<string | undefined> {
    if (!(value instanceof File)) {
      return of(existing ?? undefined);
    }
    const path = `${type}-${userId}/${value.name}`;
    return from(
      this.blobService.uploadImageAsync(
        environment.SAS_TOKEN_BLOB_STORAGE_AZURE,
        value,
        path,
      ),
    ).pipe(map(() => path));
  }

  enableThumbnailChange(): void {
    this.showThumbnailInput = true;
  }

  enableBannerChange(): void {
    this.showBannerInput = true;
  }

  onThumbnailFileChange(event: Event): void {
    this.handleFileChange(event, "thumbnail", (result) => {
      this.thumbnailPreview = result;
      this.showThumbnailInput = false;
    });
  }

  onBannerFileChange(event: Event): void {
    this.handleFileChange(event, "banner", (result) => {
      this.bannerPreview = result;
      this.showBannerInput = false;
    });
  }

  clearThumbnail(): void {
    this.controls.thumbnail.setValue(null);
    this.controls.thumbnail.markAsDirty();
    this.thumbnailPreview = null;
    this.showThumbnailInput = true;
    this.existingThumbnail = null;
  }

  clearBanner(): void {
    this.controls.banner.setValue(null);
    this.controls.banner.markAsDirty();
    this.bannerPreview = null;
    this.showBannerInput = true;
    this.existingBanner = null;
  }

  private handleFileChange(
    ev: Event,
    control: "thumbnail" | "banner",
    onLoad: (result: string | ArrayBuffer | null) => void,
  ): void {
    const input = ev.target as HTMLInputElement | null;
    const file = input?.files?.item(0);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => onLoad(reader.result);
    reader.readAsDataURL(file);

    this.controls[control].setValue(file);
    this.controls[control].markAsDirty();
  }

  urlFriendly(str: string): string {
    return str
      .toLowerCase()
      .trim()
      .replace(/ /g, "-")
      .replace(/[^a-z0-9-]+/g, "");
  }

  updateOrganizerPage(value: string) {
    const urlFriendlyValue = this.urlFriendly(value);
    this.controls.organizer_page.setValue(urlFriendlyValue, {
      emitEvent: false,
    });
  }
}
